import { useMemo, useState } from "react";
import { Archive, Boxes, ChevronDown, Factory, FileUp, History, PackageOpen, Plus, SlidersHorizontal, Store, Tags } from "lucide-react";
import { toast } from "sonner";
import type { InventoryRow, MarketplaceListing, Product, VariantAlias } from "../types";
import { archiveProduct } from "../services/inventory";
import { StatusPill } from "../components/Layout";
import { ProductImportModal } from "../components/ProductImportModal";
import { AliasManager } from "../components/AliasManager";

interface ProductsPageProps {
  rows: InventoryRow[];
  onNew: () => void;
  onMove: (row: InventoryRow) => void;
  onHistory: (row: InventoryRow) => void;
  aliases: VariantAlias[];
  listings: MarketplaceListing[];
}

export function ProductsPage({ rows, onNew, onMove, onHistory, aliases, listings }: ProductsPageProps) {
  const [expandedId, setExpandedId] = useState<string>();
  const [importOpen, setImportOpen] = useState(false);
  const [aliasRow, setAliasRow] = useState<InventoryRow>();
  const products = useMemo(() => {
    const grouped = new Map<string, { product: Product; rows: InventoryRow[] }>();
    rows.forEach((row) => {
      const current = grouped.get(row.product.id) ?? { product: row.product, rows: [] };
      current.rows.push(row);
      grouped.set(row.product.id, current);
    });
    return Array.from(grouped.values()).map((group) => ({
      ...group,
      rows: group.rows.sort((a, b) => a.variant.colorName.localeCompare(b.variant.colorName, "pt-BR")),
    }));
  }, [rows]);

  const archive = async (product: Product) => {
    if (!confirm(`Arquivar ${product.name}? O histórico será preservado.`)) return;
    await archiveProduct(product.id);
    toast.success("Produto arquivado sem apagar o histórico.");
  };

  return <div className="page-stack">
    <section className="section-heading">
      <div><span className="eyebrow">Catálogo por SKU</span><h2>Produtos e suas cores</h2><p>Cada SKU representa um produto; abra o grupo para consultar e editar o estoque de cada cor.</p></div>
      <div className="heading-actions"><button className="secondary-button" onClick={() => setImportOpen(true)}><FileUp size={18} />Importar produtos</button><button className="primary-button" onClick={onNew}><Plus size={18} />Novo produto</button></div>
    </section>

    <section className="product-catalog">
      {products.map(({ product, rows: variants }) => {
        const expanded = expandedId === product.id;
        const galpao = variants.reduce((sum, row) => sum + row.galpao.quantity, 0);
        const malharia = variants.reduce((sum, row) => sum + (row.variant.malhariaEnabled ? row.malharia?.quantity ?? 0 : 0), 0);
        const total = variants.reduce((sum, row) => sum + row.total, 0);
        const lastUpdated = Math.max(...variants.map((row) => Date.parse(row.variant.updatedAt)));
        const productListings = listings.filter((listing) => listing.productId === product.id);
        return <article className={`product-group-card ${expanded ? "expanded" : ""}`} key={product.id}>
          <button className="product-group-header" onClick={() => setExpandedId(expanded ? undefined : product.id)} aria-expanded={expanded}>
            {product.photoUrl ? <img className="product-photo" src={product.photoUrl} alt="" /> : <div className="product-monogram">{product.name.split(" ").slice(0, 2).map((word) => word[0]).join("")}</div>}
            <div className="product-group-title"><code>{product.sku}</code><h3>{product.name}</h3><span>{product.category}{productListings.length ? ` · ${productListings.length} anúncio(s) Shopee` : ""}</span></div>
            <div className="product-group-summary">
              <span><PackageOpen size={16} /><b>{variants.length}</b> cores</span>
              <span><Boxes size={16} /><b>{galpao}</b> galpão</span>
              {malharia > 0 && <span><Factory size={16} /><b>{malharia}</b> malharia</span>}
              <strong>{total}<small>total</small></strong>
            </div>
            <ChevronDown className="product-chevron" size={22} />
          </button>

          {expanded && <div className="product-color-list">
            {variants.map((row) => <div className="product-color-row" key={row.variant.id}>
              <div className="product-color-name"><i style={{ background: row.variant.colorCode }} /><div><strong>{row.variant.colorName}</strong><span>{product.sku} / {row.variant.colorName.toUpperCase()}</span></div></div>
              <div className="product-stock-value"><span>Galpão</span><strong>{row.galpao.quantity}</strong></div>
              <div className={`product-stock-value ${!row.variant.malhariaEnabled ? "disabled" : ""}`}><span>Malharia</span><strong>{row.variant.malhariaEnabled ? row.malharia?.quantity ?? 0 : "—"}</strong></div>
              <div className="product-stock-value total"><span>Total</span><strong>{row.total}</strong></div>
              <div className="product-color-actions">
                <button className="small-action" onClick={() => onMove(row)}><SlidersHorizontal size={15} />Editar estoque</button>
                <button className="small-action ghost-alias" onClick={() => setAliasRow(row)}><Tags size={15} />Aliases{aliases.filter((alias) => alias.variantId === row.variant.id).length ? ` (${aliases.filter((alias) => alias.variantId === row.variant.id).length})` : ""}</button>
                <button className="icon-button" onClick={() => onHistory(row)} title="Histórico da cor" aria-label={`Histórico de ${row.variant.colorName}`}><History size={17} /></button>
              </div>
            </div>)}
          </div>}

          {expanded && productListings.length > 0 && <div className="marketplace-listings">
            <div className="marketplace-heading"><Store size={17} /><strong>Dados dos anúncios da Shopee</strong><span>As quantidades abaixo são do anúncio e não alteram o estoque físico.</span></div>
            {productListings.map((listing) => <details key={listing.id}>
              <summary>{listing.coverImageUrl && <img src={listing.coverImageUrl} alt="" />}<div><strong>{listing.listingName}</strong><span>Anúncio {listing.externalListingId} · {listing.shopName || "Shopee"}</span></div><b>{listing.discountPrice !== undefined ? listing.discountPrice.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "Ver dados"}</b></summary>
              <div className="marketplace-details">
                <p>{listing.description || "Sem descrição no arquivo."}</p>
                <dl><div><dt>Categoria externa</dt><dd>{listing.categoryExternalId || "—"}</dd></div><div><dt>Preço</dt><dd>{listing.price?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) ?? "—"}</dd></div><div><dt>Peso</dt><dd>{listing.weightKg ?? "—"} kg</dd></div><div><dt>Dimensões</dt><dd>{[listing.lengthCm, listing.widthCm, listing.heightCm].map((item) => item ?? "—").join(" × ")} cm</dd></div><div><dt>Pré-venda</dt><dd>{listing.preorderDays ?? "—"} dias</dd></div><div><dt>Atualização Shopee</dt><dd>{listing.sourceUpdatedAt || "—"}</dd></div></dl>
              </div>
            </details>)}
          </div>}

          <footer className="product-group-footer">
            <StatusPill tone={product.status === "ativo" ? "success" : "neutral"}>{product.status}</StatusPill>
            <span>Atualizado em {new Date(lastUpdated).toLocaleDateString("pt-BR")}</span>
            <button className="icon-button danger" onClick={() => archive(product)} title="Arquivar produto" aria-label={`Arquivar ${product.name}`}><Archive size={17} /></button>
          </footer>
        </article>;
      })}
    </section>
    <ProductImportModal open={importOpen} onClose={() => setImportOpen(false)} />
    <AliasManager open={Boolean(aliasRow)} product={aliasRow?.product} variant={aliasRow?.variant} aliases={aliases} onClose={() => setAliasRow(undefined)} />
  </div>;
}
